import { graphqlClient } from '@/lib/graphql-client';
import {
  GET_HOT_WHEELS_QUERY,
  GET_HOT_WHEEL_QUERY,
  GET_WISHLIST_QUERY,
} from '../graphql/queries';
import {
  INSERT_HOT_WHEEL_MUTATION,
  UPDATE_HOT_WHEEL_MUTATION,
  DELETE_HOT_WHEEL_MUTATION,
  INSERT_WISHLIST_MUTATION,
  UPDATE_WISHLIST_MUTATION,
  DELETE_WISHLIST_MUTATION,
} from '../graphql/mutations';
import {
  HotWheel,
  HotWheelInsertInput,
  HotWheelUpdateInput,
  WishlistItem,
  WishlistInsertInput,
  WishlistUpdateInput,
  PagedHotWheels,
  PagedWishlist,
  SortField,
  SortDir,
} from '../types/hotwheels.types';

/**
 * Hot Wheels service — GraphQL CRUD over the data gateway.
 *
 * Each function returns the raw gateway response. React Query hooks in
 * `use-hotwheels.ts` wrap these for caching, loading state, and mutations.
 */

export interface HotWheelsQueryParams {
  pageNo: number;
  pageSize: number;
  where?: Record<string, any>;
  order?: { field: SortField; direction: SortDir }[];
}

export async function getHotWheels(
  params: HotWheelsQueryParams
): Promise<PagedHotWheels> {
  const data = await graphqlClient.query<{ getHotWheels: PagedHotWheels }>({
    query: GET_HOT_WHEELS_QUERY,
    variables: {
      where: params.where,
      order: params.order?.map((o) => ({ direction: o.direction, field: o.field })),
      paging: { pageNo: params.pageNo, pageSize: params.pageSize },
    },
  });
  return data.getHotWheels;
}

export async function getHotWheel(itemId: string): Promise<HotWheel | null> {
  const data = await graphqlClient.query<{ getHotWheels: PagedHotWheels }>({
    query: GET_HOT_WHEEL_QUERY,
    variables: {
      where: { ItemId: { eq: itemId } },
    },
  });
  return data.getHotWheels.items[0] ?? null;
}

export async function insertHotWheel(input: HotWheelInsertInput): Promise<{ itemId: string | null }> {
  const data = await graphqlClient.mutate<{ insertHotWheel: { itemId: string | null } }>({
    query: INSERT_HOT_WHEEL_MUTATION,
    variables: { input },
  });
  return data.insertHotWheel;
}

export async function updateHotWheel(
  itemId: string,
  input: HotWheelUpdateInput
): Promise<{ acknowledged: boolean; message: string | null }> {
  const data = await graphqlClient.mutate<{
    updateHotWheel: { acknowledged: boolean; message: string | null };
  }>({
    query: UPDATE_HOT_WHEEL_MUTATION,
    variables: {
      where: { ItemId: { eq: itemId } },
      input,
    },
  });
  return data.updateHotWheel;
}

export async function deleteHotWheel(
  itemId: string
): Promise<{ acknowledged: boolean; totalImpactedData: number }> {
  const data = await graphqlClient.mutate<{
    deleteHotWheel: { acknowledged: boolean; totalImpactedData: number };
  }>({
    query: DELETE_HOT_WHEEL_MUTATION,
    variables: { input: { ItemId: itemId, isHardDelete: true } },
  });
  return data.deleteHotWheel;
}

// ---------------------------------------------------------------------------
// Wishlist
// ---------------------------------------------------------------------------

export interface WishlistQueryParams {
  pageNo: number;
  pageSize: number;
}

export async function getWishlist(params: WishlistQueryParams): Promise<PagedWishlist> {
  const data = await graphqlClient.query<{ getWishlists: PagedWishlist }>({
    query: GET_WISHLIST_QUERY,
    variables: { paging: { pageNo: params.pageNo, pageSize: params.pageSize } },
  });
  return data.getWishlists;
}

export async function insertWishlist(
  input: WishlistInsertInput
): Promise<{ itemId: string | null }> {
  const data = await graphqlClient.mutate<{ insertWishlist: { itemId: string | null } }>({
    query: INSERT_WISHLIST_MUTATION,
    variables: { input },
  });
  return data.insertWishlist;
}

export async function updateWishlist(
  itemId: string,
  input: WishlistUpdateInput
): Promise<{ acknowledged: boolean; message: string | null }> {
  const data = await graphqlClient.mutate<{
    updateWishlist: { acknowledged: boolean; message: string | null };
  }>({
    query: UPDATE_WISHLIST_MUTATION,
    variables: { where: { ItemId: { eq: itemId } }, input },
  });
  return data.updateWishlist;
}

export async function deleteWishlist(itemId: string): Promise<{ acknowledged: boolean }> {
  const data = await graphqlClient.mutate<{
    deleteWishlist: { acknowledged: boolean };
  }>({
    query: DELETE_WISHLIST_MUTATION,
    variables: { input: { ItemId: itemId, isHardDelete: true } },
  });
  return data.deleteWishlist;
}